import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import {
  leaveTopic,
  getTopics,
  subscribeToTopicWithMessages,
} from "../../../services/tinodeApi";
import { useTinodeConnection } from "../../../hooks/useTinodeConnection";
import { useTinodeMessages } from "../../../hooks/useTinodeMessages";
import {
  endConsultationSession as endTinodeConsultationSession,
  isSessionEndMessage,
  isSessionEndedFromMessages,
  sendDoctorNotes,
  sendDoctorPrescription,
} from "../../../services/tinodeSessionService";
import {
  startConsultationSession as startConsultationSessionAPI,
  endConsultationSession as endConsultationSessionAPI,
  getConsultationSessionStatus,
} from "../../../services/telemedicineApi";
import { parseTinodeError } from "../../../utils/tinodeErrorHelper";
import TinodeLoadingScreen from "./tinodeComponent/TinodeLoadingScreen";
import TopicList from "./tinodeComponent/TopicList";
import UserInfo from "./tinodeComponent/UserInfo";
import ChatDashboardDoctor from "./tinodeComponent/ChatDashboardDoctor";
import { LuSearch, LuX } from "react-icons/lu";
import ConfirmModal from "../../../components/modalComp/ConfirmModal";
import NotFound from "../../../assets/notFound.png";
import { iterateTopicMessages } from "../../../utils/tinodeTopicHelper";
import {
  formatMessageForDashboard,
  sortMessagesBySeq,
} from "../../../utils/tinodeMessageUtils";
import { buildNotesText } from "./tinodeComponent/SendNotesDialog";
import { useParams } from "react-router-dom";
import { parseTopicFn } from "../../../services/tinodeConsultationGroupService";

const SESSION_DURATION_MS = 30 * 60 * 1000;

const TinodeChatDoctor = ({ user, drugData }) => {
  const { topicId } = useParams();

  const [selectedTopic, setSelectedTopic] = useState(null);
  const [isEndingSession, setIsEndingSession] = useState(false);
  const [endedTopics, setEndedTopics] = useState({});
  const [sessionWindowByTopic, setSessionWindowByTopic] = useState({});
  const [nowMs, setNowMs] = useState(Date.now());
  const [searchQuery, setSearchQuery] = useState("");
  const [activeFilter, setActiveFilter] = useState("semua");
  const [openModalEndChat, setOpenModalEndChat] = useState(false);
  const [unreadCounts, setUnreadCounts] = useState({});
  const [hasSelectedFromUrl, setHasSelectedFromUrl] = useState(false);

  const selectedTopicRef = useRef(null);
  const endedTopicsRef = useRef({});
  const endingTopicsRef = useRef(new Set());
  const expiryTimersRef = useRef({});
  const isChangingTopicRef = useRef(false);
  const initialLoadDoneRef = useRef(new Set());
  const startedSessionTopicsRef = useRef(new Set());
  const searchInputRef = useRef(null);

  selectedTopicRef.current = selectedTopic;

  useEffect(() => {
    endedTopicsRef.current = endedTopics;
  }, [endedTopics]);

  useEffect(() => {
    const timer = setInterval(() => {
      setNowMs(Date.now());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const currentUser = useMemo(
    () =>
      user || {
        fullname: localStorage.getItem("fullname") || "User",
        email: localStorage.getItem("email") || "",
        id: localStorage.getItem("user_id") || "",
      },
    [user],
  );

  const { tinodeRef, isReady, isLoading, error, topics, setTopics, userInfo } =
    useTinodeConnection(currentUser);

  const getErrorMessage = useCallback((error) => {
    if (error?.message?.id) return error.message.id;
    if (error?.message?.en) return error.message.en;
    if (error?.message) return error.message;
    return parseTinodeError(error) || String(error);
  }, []);

  const clearExpiryTimer = useCallback((topicName) => {
    if (!topicName) return;
    const timer = expiryTimersRef.current[topicName];
    if (timer) {
      clearTimeout(timer);
      delete expiryTimersRef.current[topicName];
    }
  }, []);

  const endSessionByTopic = useCallback(
    async (topicName, options = {}) => {
      const { silent = false } = options;

      if (!topicName || !tinodeRef.current) return;
      if (endedTopicsRef.current[topicName]) return;
      if (endingTopicsRef.current.has(topicName)) return;

      endingTopicsRef.current.add(topicName);
      if (selectedTopicRef.current === topicName) {
        setIsEndingSession(true);
      }

      try {
        await endConsultationSessionAPI({
          topic_name: topicName,
        });

        try {
          const topic = tinodeRef.current.getTopic(topicName);
          if (!topic || !topic.isSubscribed()) {
            await subscribeToTopicWithMessages(tinodeRef.current, topicName, 1);
          }

          await endTinodeConsultationSession(
            tinodeRef.current,
            topicName,
            "doctor",
          );
        } catch (publishError) {
          console.warn(
            "Failed to publish session-end marker to Tinode:",
            publishError,
          );
        }

        clearExpiryTimer(topicName);
        setEndedTopics((prev) => ({ ...prev, [topicName]: true }));
      } catch (err) {
        if (!silent) {
          alert("Gagal mengakhiri sesi: " + getErrorMessage(err));
        } else {
          console.warn("[auto-expire] failed to end session:", err);
        }
      } finally {
        endingTopicsRef.current.delete(topicName);
        if (selectedTopicRef.current === topicName) {
          setIsEndingSession(false);
        }
      }
    },
    [clearExpiryTimer, getErrorMessage, tinodeRef],
  );

  const scheduleSessionAutoEnd = useCallback(
    (topicName, expiresAt) => {
      if (!topicName || !expiresAt) return;

      const expiresAtMs = Date.parse(expiresAt);
      if (Number.isNaN(expiresAtMs)) {
        return;
      }

      clearExpiryTimer(topicName);

      const delayMs = expiresAtMs - Date.now();
      if (delayMs <= 0) {
        void endSessionByTopic(topicName, { silent: true });
        return;
      }

      expiryTimersRef.current[topicName] = setTimeout(() => {
        void endSessionByTopic(topicName, { silent: true });
      }, delayMs);
    },
    [clearExpiryTimer, endSessionByTopic],
  );

  const syncSessionStatus = useCallback(
    async (topicName) => {
      if (!topicName) return null;

      try {
        const status = await getConsultationSessionStatus({
          topic_name: topicName,
        });

        if (status?.started_at || status?.expires_at) {
          setSessionWindowByTopic((prev) => ({
            ...prev,
            [topicName]: {
              startAt: status?.started_at || null,
              expiresAt: status?.expires_at || null,
            },
          }));
        }

        if (status?.expires_at) {
          scheduleSessionAutoEnd(topicName, status.expires_at);
        }

        if (status?.status === 4) {
          startedSessionTopicsRef.current.add(topicName);
        }

        if (
          status?.status === 5 ||
          status?.status === 6 ||
          status?.can_chat === false ||
          (status?.is_expired && status?.status === 4)
        ) {
          clearExpiryTimer(topicName);
          setEndedTopics((prev) => ({ ...prev, [topicName]: true }));
        }

        return status;
      } catch (error) {
        console.warn("Failed to sync consultation session status:", error);
        return null;
      }
    },
    [clearExpiryTimer, scheduleSessionAutoEnd],
  );

  const ensureSessionStarted = useCallback(
    async (topicName) => {
      const targetTopic = topicName || selectedTopicRef.current;
      if (!targetTopic) return;

      const knownWindow = sessionWindowByTopic[targetTopic];
      const knownExpiresAtMs = Date.parse(knownWindow?.expiresAt || "");
      if (!Number.isNaN(knownExpiresAtMs) && Date.now() >= knownExpiresAtMs) {
        clearExpiryTimer(targetTopic);
        setEndedTopics((prev) => ({ ...prev, [targetTopic]: true }));
        void endSessionByTopic(targetTopic, { silent: true });
        throw new Error("Consultation session already expired");
      }

      if (startedSessionTopicsRef.current.has(targetTopic)) return;

      const syncedStatus = await syncSessionStatus(targetTopic);
      if (
        syncedStatus?.status === 5 ||
        syncedStatus?.status === 6 ||
        syncedStatus?.can_chat === false
      ) {
        clearExpiryTimer(targetTopic);
        setEndedTopics((prev) => ({ ...prev, [targetTopic]: true }));
        throw new Error("Consultation session already ended");
      }
      if (syncedStatus?.status === 4 && syncedStatus?.can_chat !== false) {
        startedSessionTopicsRef.current.add(targetTopic);
        return;
      }

      try {
        const session = await startConsultationSessionAPI({
          topic_name: targetTopic,
        });

        if (session?.started_at || session?.expires_at) {
          setSessionWindowByTopic((prev) => ({
            ...prev,
            [targetTopic]: {
              startAt: session?.started_at || null,
              expiresAt: session?.expires_at || null,
            },
          }));
        }

        if (session?.expires_at) {
          scheduleSessionAutoEnd(targetTopic, session.expires_at);
        }

        startedSessionTopicsRef.current.add(targetTopic);
      } catch (error) {
        const msg = getErrorMessage(error).toLowerCase();
        if (msg.includes("sudah selesai") || msg.includes("already ended")) {
          clearExpiryTimer(targetTopic);
          setEndedTopics((prev) => ({ ...prev, [targetTopic]: true }));
        }
        throw error;
      }
    },
    [
      clearExpiryTimer,
      endSessionByTopic,
      getErrorMessage,
      scheduleSessionAutoEnd,
      sessionWindowByTopic,
      syncSessionStatus,
    ],
  );

  const handleMessageReceived = useCallback(
    (msg) => {
      const topicName = selectedTopicRef.current;
      if (!topicName) return;
      if (isSessionEndMessage(msg)) {
        clearExpiryTimer(topicName);
        setEndedTopics((prev) => ({ ...prev, [topicName]: true }));
      }
    },
    [clearExpiryTimer],
  );

  const {
    messages,
    setMessages,
    messagesEndRef,
    messagesContainerRef,
    messageSeqSetRef,
    optimisticMessagesRef,
    loadTopicMessages,
    sendOptimisticMessage,
    removeOptimisticMessage,
    loadOlderMessages,
  } = useTinodeMessages(tinodeRef, selectedTopic, handleMessageReceived);

  const currentSessionWindow = useMemo(() => {
    if (!selectedTopic) return null;

    const fromAPI = sessionWindowByTopic[selectedTopic];
    if (fromAPI?.startAt || fromAPI?.expiresAt) {
      return fromAPI;
    }

    const currentUID = tinodeRef.current?.getCurrentUserID?.();
    if (!currentUID || !Array.isArray(messages) || messages.length === 0) {
      return null;
    }

    let firstDoctorMessageMs = null;
    for (const msg of messages) {
      if (msg?.from !== currentUID) continue;
      const ts =
        typeof msg?.timestamp === "number"
          ? msg.timestamp
          : Date.parse(msg?.ts || msg?.timestamp || "");
      if (Number.isNaN(ts)) continue;
      if (firstDoctorMessageMs === null || ts < firstDoctorMessageMs) {
        firstDoctorMessageMs = ts;
      }
    }

    if (firstDoctorMessageMs === null) return null;

    return {
      startAt: new Date(firstDoctorMessageMs).toISOString(),
      expiresAt: new Date(
        firstDoctorMessageMs + SESSION_DURATION_MS,
      ).toISOString(),
    };
  }, [selectedTopic, sessionWindowByTopic, tinodeRef, messages]);

  const isCurrentSessionExpiredByTime = useMemo(() => {
    const expiresAtMs = Date.parse(currentSessionWindow?.expiresAt || "");
    if (Number.isNaN(expiresAtMs)) return false;
    return nowMs >= expiresAtMs;
  }, [currentSessionWindow, nowMs]);

  const isCurrentSessionOver = selectedTopic
    ? !!endedTopics[selectedTopic] || isCurrentSessionExpiredByTime
    : false;

  useEffect(() => {
    if (!selectedTopic) return;
    if (!isCurrentSessionExpiredByTime) return;
    if (endedTopics[selectedTopic]) return;

    clearExpiryTimer(selectedTopic);
    setEndedTopics((prev) => ({ ...prev, [selectedTopic]: true }));
    void endSessionByTopic(selectedTopic, { silent: true });
  }, [
    clearExpiryTimer,
    endedTopics,
    endSessionByTopic,
    isCurrentSessionExpiredByTime,
    selectedTopic,
  ]);

  const refreshTopics = useCallback(() => {
    if (!tinodeRef.current) return;
    const list = getTopics(tinodeRef.current).filter((t) => {
      const name = typeof t === "string" ? t : t.topic || t.id || t.name;
      return name && name !== "me" && !name.startsWith("fnd");
    });
    setTopics(list);
  }, [tinodeRef, setTopics]);

  const backgroundSubscribeTopic = useCallback(
    async (topicName) => {
      if (!tinodeRef.current || !topicName) return;
      if (topicName === "me" || topicName.startsWith("fnd")) return;
      try {
        const existing = tinodeRef.current.getTopic(topicName);
        if (existing && existing.isSubscribed()) {
          if (!existing.onData) {
            existing.onData = (msg) => {
              if (!msg?.seq) return;
              if (selectedTopicRef.current === topicName) return;
              setUnreadCounts((prev) => ({
                ...prev,
                [topicName]: (prev[topicName] || 0) + 1,
              }));
            };
          }
          return;
        }
        const { subscribeToTopicWithMessages } =
          await import("../../../services/tinodeApi");
        const subscribedTopic = await subscribeToTopicWithMessages(
          tinodeRef.current,
          topicName,
          1,
        );
        if (!subscribedTopic) return;
        subscribedTopic.onData = (msg) => {
          if (!msg?.seq) return;
          if (selectedTopicRef.current === topicName) return;
          setUnreadCounts((prev) => ({
            ...prev,
            [topicName]: (prev[topicName] || 0) + 1,
          }));
        };
      } catch (_) {}
    },
    [tinodeRef],
  );

  useEffect(() => {
    const handler = (e) => {
      const { topicName } = e.detail || {};
      if (!topicName || topicName === "me") return;
      backgroundSubscribeTopic(topicName);
      // refreshTopics() tidak dipanggil di sini karena forceRefreshTopics di
      // useTinodeConnection (yang sudah di-trigger oleh me.onPres/onContactUpdate)
      // akan mergeEnrichedTopics setelah network call selesai. Memanggil full-replace
      // di sini sebelum fetch selesai justru menyebabkan topic baru hilang sementara.
    };
    window.addEventListener("tinode-new-topic", handler);
    return () => window.removeEventListener("tinode-new-topic", handler);
  }, [backgroundSubscribeTopic]);

  const injectNewMessages = useCallback(
    (topicName) => {
      if (!tinodeRef.current || !topicName) return;
      const topic = tinodeRef.current.getTopic(topicName);
      if (!topic) return;

      const newMessages = [];
      const newSeqSet = new Set();

      iterateTopicMessages(topic, (m) => {
        if (!m?.seq) return;
        if (isSessionEndMessage(m)) return;
        if (messageSeqSetRef.current.has(m.seq) || newSeqSet.has(m.seq)) return;
        messageSeqSetRef.current.add(m.seq);
        newSeqSet.add(m.seq);
        newMessages.push(formatMessageForDashboard(m, topicName));
      });

      if (newMessages.length > 0) {
        setMessages((prev) => {
          const map = new Map();

          prev.forEach((msg) => {
            if (msg.seq && !map.has(msg.seq)) {
              map.set(msg.seq, msg);
            }
          });

          newMessages.forEach((msg) => {
            if (msg.seq && !map.has(msg.seq)) {
              map.set(msg.seq, msg);
            }
          });

          return sortMessagesBySeq(Array.from(map.values()));
        });
      }
    },
    [tinodeRef, messageSeqSetRef, setMessages],
  );

  const handleSendNotes = useCallback(
    async ({ diagnosa, saranList }) => {
      if (!selectedTopic || !tinodeRef.current) return;
      if (endedTopicsRef.current[selectedTopic]) return;

      await ensureSessionStarted(selectedTopic);

      const name = userInfo?.fullname || currentUser.fullname || "Dokter";
      const greetingText = buildNotesText(name, diagnosa, saranList);

      const greetResult = await sendDoctorNotes(
        tinodeRef.current,
        selectedTopic,
        greetingText,
      );

      if (greetResult?.seq) {
        await new Promise((r) => setTimeout(r, 600));
        injectNewMessages(selectedTopic);
      }
    },
    [
      selectedTopic,
      tinodeRef,
      userInfo,
      currentUser.fullname,
      injectNewMessages,
      ensureSessionStarted,
    ],
  );

  const handleSendPrescription = useCallback(
    async (prescriptionList) => {
      if (!selectedTopic || !tinodeRef.current) return null;
      if (endedTopicsRef.current[selectedTopic]) return null;

      await ensureSessionStarted(selectedTopic);

      const result = await sendDoctorPrescription(
        tinodeRef.current,
        selectedTopic,
        prescriptionList,
      );

      if (result?.seq) {
        await new Promise((r) => setTimeout(r, 600));
        injectNewMessages(selectedTopic);
      }

      return result;
    },
    [selectedTopic, tinodeRef, injectNewMessages, ensureSessionStarted],
  );

  const filteredTopics = useMemo(() => {
    let result = topics;

    if (activeFilter === "aktif") {
      result = result.filter((t) => {
        const name =
          typeof t === "string" ? t : t.topic || t.id || t.name || "";
        return !endedTopics[name];
      });
    } else if (activeFilter === "selesai") {
      result = result.filter((t) => {
        const name =
          typeof t === "string" ? t : t.topic || t.id || t.name || "";
        return !!endedTopics[name];
      });
    }

    const q = searchQuery.trim().toLowerCase();
    if (q) {
      result = result.filter((t) => {
        const topicName =
          typeof t === "string" ? t : t.topic || t.id || t.name || "";
        const rawTopicMeta =
          typeof t === "object"
            ? t.public?.fn ||
              (typeof t.name === "string" && t.name.trim().startsWith("{")
                ? t.name
                : "")
            : "";
        const customerName = (
          parseTopicFn(rawTopicMeta).customerName || ""
        ).toLowerCase();
        const lastMsg = (t.lastMessage || "").toLowerCase();
        return (
          customerName.includes(q) ||
          lastMsg.includes(q) ||
          topicName.toLowerCase().includes(q)
        );
      });
    }

    return result;
  }, [topics, searchQuery, activeFilter, endedTopics]);

  useEffect(() => {
    if (!selectedTopic) setMessages([]);
  }, [selectedTopic, setMessages]);

  useEffect(() => {
    if (!isReady || !selectedTopic || !tinodeRef.current) return;
    if (!initialLoadDoneRef.current.has(selectedTopic)) return;
    const timer = setTimeout(() => {
      loadTopicMessages(selectedTopic).catch(() => {});
    }, 500);
    return () => clearTimeout(timer);
  }, [isReady, selectedTopic, loadTopicMessages]);

  useEffect(() => {
    if (!selectedTopic) return;
    void syncSessionStatus(selectedTopic);
  }, [selectedTopic, syncSessionStatus]);

  useEffect(() => {
    if (!selectedTopic || !tinodeRef.current) return;
    const timer = setTimeout(() => {
      const topic = tinodeRef.current?.getTopic(selectedTopic);
      if (!topic) return;
      if (isSessionEndedFromMessages(topic)) {
        clearExpiryTimer(selectedTopic);
        setEndedTopics((prev) => ({ ...prev, [selectedTopic]: true }));
      }
    }, 800);
    return () => clearTimeout(timer);
  }, [clearExpiryTimer, selectedTopic, tinodeRef]);

  useEffect(() => {
    Object.keys(endedTopics).forEach((topicName) => {
      if (endedTopics[topicName]) {
        clearExpiryTimer(topicName);
      }
    });
  }, [endedTopics, clearExpiryTimer]);

  useEffect(() => {
    return () => {
      Object.values(expiryTimersRef.current).forEach((timer) =>
        clearTimeout(timer),
      );
      expiryTimersRef.current = {};
    };
  }, []);

  const handleTopicClick = async (topicName) => {
    if (!topicName || !tinodeRef.current) return;
    if (selectedTopic === topicName) return;
    if (isChangingTopicRef.current) return;

    setUnreadCounts((prev) => ({ ...prev, [topicName]: 0 }));

    isChangingTopicRef.current = true;
    try {
      setMessages([]);
      setSelectedTopic(null);

      if (selectedTopic) {
        const prev = tinodeRef.current.getTopic(selectedTopic);
        if (prev?.isSubscribed()) {
          try {
            await leaveTopic(tinodeRef.current, selectedTopic, false);
          } catch (_) {}
        }
      }

      const topicObj = await loadTopicMessages(topicName);
      if (topicObj) {
        setSelectedTopic(topicName);
        initialLoadDoneRef.current.add(topicName);
        await syncSessionStatus(topicName);
        setTimeout(() => {
          try {
            if (topicObj?.seq > 0) {
              topicObj.noteRead(topicObj.seq);
              setTimeout(refreshTopics, 200);
            }
          } catch (_) {}
        }, 800);
      }
    } catch (err) {
      alert(parseTinodeError(err) || "Failed to open chat");
    } finally {
      isChangingTopicRef.current = false;
    }
  };

  const handleEndSession = async () => {
    if (
      !selectedTopic ||
      !tinodeRef.current ||
      isEndingSession ||
      isCurrentSessionOver
    )
      return;
    await endSessionByTopic(selectedTopic, { silent: false });
    setOpenModalEndChat(false);
  };

  useEffect(() => {
    const selectTopicFromUrl = async () => {
      if (!isReady || !topicId || hasSelectedFromUrl || !tinodeRef.current)
        return;

      const topicExists = topics.some((t) => {
        const name =
          typeof t === "string" ? t : t.topic || t.id || t.name || "";
        return name === topicId;
      });

      if (topicExists) {
        console.log("Auto-selecting topic from URL:", topicId);
        await handleTopicClick(topicId);
        setHasSelectedFromUrl(true);
      } else {
        try {
          await backgroundSubscribeTopic(topicId);
          setTimeout(async () => {
            if (!hasSelectedFromUrl) {
              await handleTopicClick(topicId);
              setHasSelectedFromUrl(true);
            }
          }, 1000);
        } catch (error) {
          console.warn("Failed to auto-select topic from URL:", error);
        }
      }
    };

    selectTopicFromUrl();
  }, [
    isReady,
    topicId,
    topics,
    hasSelectedFromUrl,
    handleTopicClick,
    backgroundSubscribeTopic,
  ]);

  useEffect(() => {
    setHasSelectedFromUrl(false);
  }, [topicId]);

  if (isLoading)
    return <TinodeLoadingScreen message="Menghubungkan ke server chat..." />;

  if (error)
    return (
      <TinodeLoadingScreen
        error={error}
        onRetry={() => window.location.reload()}
      />
    );

  if (!currentUser) {
    return (
      <div style={{ padding: "20px", textAlign: "center" }}>
        <h1>Access Denied</h1>
        <p>Please login first</p>
      </div>
    );
  }

  return (
    <div className="w-full h-full flex flex-col">
      <div
        className="w-full h-full shadow-md overflow-hidden"
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 3fr",
          height: "100vh",
        }}
      >
        <div className="bg-white rounded-l-lg border-r flex flex-col h-full overflow-hidden">
          <div className="p-3 border-b flex-shrink-0">
            <UserInfo
              user={currentUser}
              userInfo={userInfo}
              tinodeRef={tinodeRef}
            />
          </div>

          <div className="px-2 py-3 border-b flex-shrink-0 flex flex-col gap-2">
            <div className="relative flex items-center">
              <LuSearch
                size={18}
                className="absolute left-3 text-gray-400 pointer-events-none"
              />
              <input
                ref={searchInputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Cari nama pasien atau group..."
                className="w-full pl-9 pr-2 py-2.5 text-md border border-gray-200 rounded-full bg-gray-50 focus:bg-white focus:border-secondary focus:ring-1 focus:ring-secondary outline-none transition-all"
              />
              {searchQuery && (
                <button
                  onClick={() => {
                    setSearchQuery("");
                    searchInputRef.current?.focus();
                  }}
                  className="absolute right-2.5 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <LuX size={16} />
                </button>
              )}
            </div>

            <div className="flex gap-1.5">
              {[
                { key: "semua", label: "Semua" },
                { key: "aktif", label: "Aktif" },
                { key: "selesai", label: "Selesai" },
              ].map(({ key, label }) => (
                <button
                  key={key}
                  onClick={() => setActiveFilter(key)}
                  className={`px-3 py-1.5 rounded-full text-md font-medium transition-all ${
                    activeFilter === key
                      ? "bg-secondary text-white"
                      : "bg-slate-100 text-slate-500 hover:text-primary hover:bg-slate-200"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
            <div className="flex justify-between items-center flex-shrink-0 mt-1">
              <p className="text-slate-500 text-md font-medium">
                {searchQuery
                  ? `Hasil pencarian (${filteredTopics.length})`
                  : activeFilter === "aktif"
                    ? `Konsultasi Aktif (${filteredTopics.length})`
                    : activeFilter === "selesai"
                      ? `Konsultasi Selesai (${filteredTopics.length})`
                      : `Semua Konsultasi (${topics.length})`}
              </p>
            </div>
          </div>

          <div className="flex-1 px-2 overflow-y-auto">
            {filteredTopics.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-full text-center p-5 gap-2">
                <div className="text-3xl">
                  {searchQuery
                    ? "🔍"
                    : activeFilter === "aktif"
                      ? "✅"
                      : activeFilter === "selesai"
                        ? "🏁"
                        : "💬"}
                </div>
                <p className="text-xs text-gray-400">
                  {searchQuery
                    ? `Tidak ada hasil untuk "${searchQuery}"`
                    : activeFilter === "aktif"
                      ? "Tidak ada konsultasi yang sedang aktif."
                      : activeFilter === "selesai"
                        ? "Belum ada konsultasi yang selesai."
                        : "Belum ada konsultasi."}
                </p>
                {!searchQuery && activeFilter === "semua" && (
                  <p className="text-xs text-gray-300">
                    Konsultasi akan muncul otomatis saat ada pasien yang
                    menghubungi.
                  </p>
                )}
              </div>
            ) : (
              <div className="-mt-3">
                <TopicList
                  topics={filteredTopics}
                  selectedTopic={selectedTopic}
                  onTopicClick={handleTopicClick}
                  endedTopics={endedTopics}
                  unreadCounts={unreadCounts}
                />
              </div>
            )}
          </div>
        </div>

        <div className="bg-white flex flex-col h-full overflow-hidden">
          {selectedTopic ? (
            <div className="flex-1 flex flex-col overflow-hidden">
              <div className="flex-1 overflow-hidden">
                <ChatDashboardDoctor
                  selectedTopic={selectedTopic}
                  topics={topics}
                  messages={messages}
                  messagesEndRef={messagesEndRef}
                  messagesContainerRef={messagesContainerRef}
                  messageSeqSetRef={messageSeqSetRef}
                  optimisticMessagesRef={optimisticMessagesRef}
                  sendOptimisticMessage={sendOptimisticMessage}
                  removeOptimisticMessage={removeOptimisticMessage}
                  tinodeRef={tinodeRef}
                  onBack={() => setSelectedTopic(null)}
                  loadOlderMessages={loadOlderMessages}
                  isCurrentSessionOver={isCurrentSessionOver}
                  // handleSendNotes={handleSendNotes}
                  setOpenModalEndChat={setOpenModalEndChat}
                  isEndingSession={isEndingSession}
                  onSend={handleSendNotes}
                  onSendPrescription={handleSendPrescription}
                  beforeSendMessage={ensureSessionStarted}
                  sessionWindow={currentSessionWindow}
                />
              </div>
            </div>
          ) : (
            <div className="h-full w-full flex items-center justify-center bg-indigo-50">
              <div className="flex flex-col items-center justify-center text-slate-700 px-4">
                <img src={NotFound} alt="Not Found" className="w-1/2 mb-3" />
                <h1 className="font-semibold text-lg text-center">
                  Pilih konsultasi untuk memulai
                </h1>
                <p className="text-center text-md mt-2">
                  Pilih dari daftar di sebelah kiri atau cari nama pasien.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      <ConfirmModal
        open={openModalEndChat}
        setOpen={setOpenModalEndChat}
        title={"Akhiri Sesi?"}
        subTitle={"Apakah Anda yakin ingin mengakhiri sesi konsultasi ini?"}
        handleConfirm={handleEndSession}
      />
    </div>
  );
};

export default TinodeChatDoctor;
